import React, { useState, useRef, useEffect } from 'react';
import { ApiEndpoint as ApiEndpointType } from '../data/apiEndpoints';
import './ApiEndpoint.css';

interface ApiEndpointProps {
  endpoint: ApiEndpointType;
  authToken: string;
  isHighlighted?: boolean;
}

const ApiEndpoint: React.FC<ApiEndpointProps> = ({ endpoint, authToken, isHighlighted }) => {
  const [activeTab, setActiveTab] = useState<'request' | 'response'>('request');
  const [expanded, setExpanded] = useState(false);
  const endpointRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHighlighted && endpointRef.current) {
      setExpanded(true);
      const timer = setTimeout(() => {
        // Highlighting effect will be removed automatically via CSS animation
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isHighlighted]);

  const methodColors: { [key: string]: string } = {
    GET: '#4A90E2',
    POST: '#27ae60',
    PUT: '#f39c12',
    DELETE: '#e74c3c',
    PATCH: '#9b59b6'
  };

  const usedBy = endpoint.usedBy ?? [];
  const app = endpoint.app ?? 'terrain-pro-api';
  const authRequired = endpoint.authRequired ?? true;
  const authScheme = endpoint.authScheme ?? (authRequired ? 'Bearer' : 'none');
  const contentType = endpoint.contentType ?? (
    (endpoint.parameters ?? []).some(p => p.type === 'file')
      ? 'multipart/form-data'
      : 'application/json'
  );

  const appLabels: { [key: string]: string } = {
    'terrain-pro-api': 'Terrain Pro API',
    'terrain-pro-legacy': 'Legacy',
    'vec-tiles': 'Vector Tiles',
    'gen1-plantpredict': 'PlantPredict Proxy'
  };

  return (
    <div
      ref={endpointRef}
      className={`api-endpoint ${isHighlighted ? 'highlighted' : ''}`}
    >
      <div className="endpoint-header" onClick={() => setExpanded(!expanded)}>
        <div className="endpoint-main">
          <span
            className="method-badge"
            style={{ backgroundColor: methodColors[endpoint.method] }}
          >
            {endpoint.method}
          </span>
          <span className="endpoint-path">{endpoint.path}</span>
          {endpoint.step && (
            <span className="step-badge">STEP {endpoint.step}</span>
          )}
          {endpoint.reportDemoStep && (
            <span className="step-badge report-step-badge">REPORT STEP {endpoint.reportDemoStep}</span>
          )}
          {app !== 'terrain-pro-api' && app !== 'terrain-pro-legacy' && (
            <span className="app-badge">{appLabels[app]}</span>
          )}
          {usedBy.includes('api-demo') && <span className="used-by-badge api-demo">API Demo</span>}
          {usedBy.includes('report-demo') && <span className="used-by-badge used-by-report-demo">Report Demo</span>}
          {authScheme === 'Bearer-PP' && <span className="auth-badge bearer-pp" title="Non-standard Authorization scheme">Bearer-PP</span>}
        </div>
        <button className="expand-button">
          {expanded ? '−' : '+'}
        </button>
      </div>

      {expanded && (
        <div className="endpoint-body">
          <h3>{endpoint.title}</h3>
          <p className="endpoint-description">{endpoint.description}</p>

          <div className="endpoint-meta">
            <div className="endpoint-meta-item">
              <span className="endpoint-meta-label">Content-Type</span>
              <code>{contentType}</code>
            </div>
            <div className="endpoint-meta-item">
              <span className="endpoint-meta-label">Auth</span>
              <span>
                {authScheme === 'none' ? 'Not required' : `Authorization: ${authScheme} <token>`}
                {!authRequired && authScheme !== 'none' ? ' (sent, but not enforced)' : ''}
              </span>
            </div>
            {endpoint.usedByNote && (
              <div className="endpoint-meta-item endpoint-meta-note">
                <span className="endpoint-meta-label">Integration</span>
                <span>{endpoint.usedByNote}</span>
              </div>
            )}
            {endpoint.authNote && (
              <div className="endpoint-meta-item endpoint-meta-note">
                <span className="endpoint-meta-label">Auth note</span>
                <span>{endpoint.authNote}</span>
              </div>
            )}
          </div>

          <div className="tabs">
            <button
              className={`tab ${activeTab === 'request' ? 'active' : ''}`}
              onClick={() => setActiveTab('request')}
            >
              Request
            </button>
            <button
              className={`tab ${activeTab === 'response' ? 'active' : ''}`}
              onClick={() => setActiveTab('response')}
            >
              Response
            </button>
          </div>

          {activeTab === 'request' && (
            <div className="tab-content">
              {endpoint.parameters && endpoint.parameters.length > 0 ? (
                <>
                  <h4>Parameters</h4>
                  <table className="parameters-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Required</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {endpoint.parameters.map((param) => (
                        <tr key={param.name}>
                          <td><code>{param.name}</code></td>
                          <td><span className="type-badge">{param.type}</span></td>
                          <td>{param.required ? '✓' : '−'}</td>
                          <td>{param.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <p className="no-params">No parameters required</p>
              )}
            </div>
          )}

          {activeTab === 'response' && (
            <div className="tab-content">
              <h4>Example Response</h4>
              <pre className="response-example">
                {endpoint.response || 'No example available'}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApiEndpoint;

